import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, ChevronRight, FolderKanban, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import { fmt } from "../lib/utils";
import Modal from "./common/Modal";

const RISK_TONE: Record<string, string> = {
  "HØY": "bg-neg/10 text-neg",
  "MIDDELS": "bg-flag/10 text-flag",
  "LAV": "bg-pos/10 text-pos",
};

export default function ProjectsView() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", currency: "NOK", contract_type: "NS8405" });

  const user = JSON.parse(localStorage.getItem("contrack_user") || "null");
  const canCreate = user?.role === "admin" || user?.role === "cfo";

  const load = () => {
    setLoading(true);
    api.get("/portfolio/overview")
      .then(r => setProjects(r.data.projects || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createProject = async () => {
    if (!form.name.trim()) { setError(t("projects.name_required")); return; }
    setSaving(true); setError(null);
    try {
      const r = await api.post("/projects/", form);
      setShowNew(false);
      setForm({ name: "", currency: "NOK", contract_type: "NS8405" });
      const newId = r.data?.project?.id;
      if (newId) navigate(`/projects/${newId}`);
      else load();
    } catch (e: any) {
      setError(getErrorMessage(e, t("projects.create_failed")));
    } finally {
      setSaving(false);
    }
  };

  const riskLabel = (r: string) =>
    r === "HØY" ? t("portfolio.hoy_risiko") : r === "MIDDELS" ? t("portfolio.middels") : t("portfolio.lav");

  return (
    <div className="animate-fade-up">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-greeting text-night">{t("nav.prosjekter")}</h1>
          <p className="text-[13.5px] text-muted mt-1">{t("projects.subtitle", { count: projects.length })}</p>
        </div>
        {canCreate && (
          <button onClick={() => { setError(null); setShowNew(true); }} className="cx-btn-dark shrink-0">
            <Plus className="w-4 h-4" /> {t("projects.new")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader className="w-6 h-6 animate-spin text-faint" /></div>
      ) : projects.length === 0 ? (
        <div className="cx-card p-12 text-center">
          <FolderKanban className="w-8 h-8 text-faint mx-auto mb-3" />
          <p className="text-[15px] font-bold text-night">{t("portfolio.ingen_aktive_prosjekter")}</p>
          <p className="text-[13px] text-muted mt-1 mb-4">{t("portfolio.legg_til_forste")}</p>
          {canCreate && (
            <button onClick={() => setShowNew(true)} className="cx-btn-dark mx-auto">
              <Plus className="w-4 h-4" /> {t("projects.new")}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p: any, i: number) => {
            const f = p.fakturaer || {};
            return (
              <div
                key={p.project_id}
                onClick={() => navigate(`/projects/${p.project_id}`)}
                style={{ animationDelay: `${i * 40}ms` }}
                className="cx-card p-5 cursor-pointer hover:shadow-pop hover:-translate-y-0.5 transition-all animate-fade-up"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-[16px] font-bold text-night truncate">{p.project_name}</h3>
                      <span className={`cx-chip ${RISK_TONE[p.hoeyeste_risiko] || RISK_TONE["LAV"]}`}>{riskLabel(p.hoeyeste_risiko)}</span>
                    </div>
                    <p className="text-[12.5px] text-muted truncate">
                      {p.vendors_grouped?.map((v: any) => v.vendor_name).join(" · ") || t("portfolio.ingen_leverandorer")}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-faint shrink-0 mt-1" />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-line">
                  <div>
                    <p className="text-[11px] text-faint font-medium">{t("portfolio.total_eksponering")}</p>
                    <p className="text-[15px] font-bold text-night tabular mt-0.5">{fmt(p.total_eksponering, p.currency)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-faint font-medium">{t("portfolio.flagget")}</p>
                    <p className="text-[15px] font-bold text-neg tabular mt-0.5">{f.flagged || 0}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-faint font-medium">{t("portfolio.fakturaer")}</p>
                    <p className="text-[15px] font-bold text-night tabular mt-0.5">{f.total || 0}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Nytt prosjekt-modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title={t("projects.new")} subtitle={t("projects.new_sub")}>
        <div className="space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("projects.name_label")}</label>
            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              onKeyDown={e => { if (e.key === "Enter") createProject(); }}
              placeholder={t("projects.name_ph")}
              className="field-ios" autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("projects.currency_label")}</label>
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="field-ios">
                <option value="NOK">NOK</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="AED">AED</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("projects.contract_type_label")}</label>
              <select value={form.contract_type} onChange={e => setForm({ ...form, contract_type: e.target.value })} className="field-ios">
                <option value="NS8405">NS 8405</option>
                <option value="NS8407">NS 8407</option>
                <option value="FIDIC">FIDIC</option>
              </select>
            </div>
          </div>
          {error && <p className="text-[13px] text-neg">{error}</p>}
          <button onClick={createProject} disabled={saving} className="cx-btn-dark w-full py-3">
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("projects.create")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
